import React, { useState, useEffect, useReducer, createContext } from 'react';
import { isMobile, osName } from 'react-device-detect';

export const GlobalStateContext = createContext();
export const GlobalDispatchContext = createContext();

import useInteraction from 'use-interaction';
import { applyThemeCSSVariables } from '@utils/theme';

const initialState = {
    device: {
        os: null, // 'iOS', 'Android', 'Windows', 'Mac OS', 'Linux', etc.
    },
    from: null,
    header: {
        full: false,
        mini: false,
        transition: false,
    },
    luminosity: 'dark',
    mode: null, // 'list', 'view', 'base' - for collection/photograph/404 pages
    overflow: true,
    overlay: {
        show: null, // null, true or false
    },
    pointer: {
        type: null,
        history: [],
        accuracy: null,
    },
    scroll: 0,
    swipe: null, // 'up' or 'down' - last swipe direction for photograph page
    view: 'viewer', // 'viewer' or 'panel' - for photograph page only
};

function reducer(state, { type, payload } = {}) {
    switch (type) {
        case 'SET_DEVICE': {
            return {
                ...state,
                device: {
                    ...state.device,
                    ...payload,
                },
            };
        }
        case 'SET_FROM': {
            return {
                ...state,
                from: payload.from,
            };
        }
        case 'SET_HEADER': {
            return {
                ...state,
                header: {
                    ...state.header,
                    ...payload,
                },
            };
        }
        case 'SET_MODE': {
            return {
                ...state,
                mode: payload,
            };
        }
        case 'SET_OVERFLOW': {
            return {
                ...state,
                overflow: payload.overflow,
            };
        }
        case 'SET_POINTER': {
            return {
                ...state,
                pointer: {
                    ...state.pointer,
                    ...payload,
                },
            };
        }
        case 'SET_SCROLL': {
            return {
                ...state,
                scroll: payload.scroll,
            };
        }
        case 'SET_SWIPE': {
            return {
                ...state,
                swipe: payload,
            };
        }
        case 'SET_THEME': {
            return {
                ...state,
                luminosity: payload.luminosity,
            };
        }
        case 'SET_VIEW': {
            return {
                ...state,
                view: payload,
            };
        }
        case 'TOGGLE_OVERLAY': {
            return {
                ...state,
                overlay: {
                    ...state.overlay,
                    show: !state.overlay.show,
                },
            };
        }
        case 'TOGGLE_THEME': {
            const luminosity = state.luminosity === 'light' ? 'dark' : 'light';
            window.localStorage.setItem('luminosity', luminosity);
            return {
                ...state,
                luminosity,
            };
        }
        default: {
            throw new Error('undefined action type');
        }
    }
}

const prefersDarkMode = () => typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches === true;

const getLuminosity = () => {
    if (typeof window !== 'undefined' && typeof window.localStorage.getItem('luminosity') === 'string')
        return window.localStorage.getItem('luminosity');
    else if (prefersDarkMode()) return 'dark';
    return 'light';
};

const GlobalContextProvider = ({ children }) => {
    const [state, dispatch] = useReducer(reducer, initialState);

    const [luminosity, setLuminosity] = useState(getLuminosity());

    const [pointerType, pointerHistory, pointerAccuracy] = useInteraction({
        initial: isMobile ? 'touch' : 'mouse',
    });

    // Expose context value to global window object for debugging
    useEffect(() => {
        window.context = state;
        return () => {
            delete window.context; // Clean up on unmount
        };
    }, [state]);

    // Initialize device OS on mount (client-side only to avoid SSR hydration mismatch)
    useEffect(() => {
        dispatch({ type: 'SET_DEVICE', payload: { os: osName } });
    }, []);

    // Initialize theme on mount
    useEffect(() => {
        dispatch({ type: 'SET_THEME', payload: { luminosity } });
    }, [luminosity]);

    // Apply CSS variables whenever theme changes (from state, not local)
    useEffect(() => {
        applyThemeCSSVariables(state.luminosity);
    }, [state.luminosity]);

    useEffect(() => {
        dispatch({
            type: 'SET_POINTER',
            payload: {
                type: pointerType,
                history: pointerHistory,
                accuracy: pointerAccuracy,
            },
        });
    }, [pointerType, pointerHistory, pointerAccuracy]);

    return (
        <GlobalStateContext.Provider value={state}>
            <GlobalDispatchContext.Provider value={dispatch}>{children}</GlobalDispatchContext.Provider>
        </GlobalStateContext.Provider>
    );
};

export default GlobalContextProvider;
